import React, { useCallback, useEffect, useState } from "react";
import {
  getAssignedDisbursementAdvances,
  confirmAdvanceDisbursement,
  reportAdvanceNotDisbursed,
  returnAdvanceToHr,
} from "../../api/advances";
import { mapAdvanceRow } from "../../utils/dataMappers";
import "./AdvanceDisbursementRouting.css";

function formatAmount(amount) {
  const value = Number(amount);
  if (Number.isNaN(value)) return "—";
  return `${value.toLocaleString("ar-EG")} ج.م`;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("ar-EG");
}

function advanceTypeLabel(row) {
  return Number(row.installments) > 1 ? "سلفة مقسطة" : "سلفة عادية";
}

export default function AdvanceDisbursementConfirm() {
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [confirmingId, setConfirmingId] = useState(null);
  const [busyAction, setBusyAction] = useState("");
  const [actionError, setActionError] = useState("");

  const loadAdvances = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const rows = await getAssignedDisbursementAdvances();
      const list = (Array.isArray(rows) ? rows : rows?.items || []).map(
        mapAdvanceRow,
      );
      setAdvances(list);
    } catch (err) {
      setLoadError(
        err?.response?.data?.message ||
          err?.message ||
          "تعذر تحميل السلف بانتظار تأكيد الصرف",
      );
      setAdvances([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdvances();
  }, [loadAdvances]);

  const handleConfirm = async (advance) => {
    if (!advance?.id || confirmingId) return;
    const ok = window.confirm(
      `تأكيد أن الموظف ${advance.employeeName || ""} استلم مبلغ ${formatAmount(advance.amount)}؟`,
    );
    if (!ok) return;

    setConfirmingId(advance.id);
    setBusyAction("confirm");
    setActionError("");
    try {
      await confirmAdvanceDisbursement(advance.id);
      await loadAdvances();
    } catch (err) {
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          "تعذر تأكيد صرف السلفة",
      );
    } finally {
      setConfirmingId(null);
      setBusyAction("");
    }
  };

  const handleNotDisbursed = async (advance) => {
    if (!advance?.id || confirmingId) return;
    const ok = window.confirm(
      `تأكيد أن سلفة ${advance.employeeName || "الموظف"} بمبلغ ${formatAmount(advance.amount)} لم تُصرف؟ ستُعاد للمحاسب العام لإعادة التوجيه أو التأجيل.`,
    );
    if (!ok) return;

    setConfirmingId(advance.id);
    setBusyAction("not-disbursed");
    setActionError("");
    try {
      await reportAdvanceNotDisbursed(advance.id);
      await loadAdvances();
    } catch (err) {
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          "تعذر الإبلاغ أن السلفة لم تُصرف",
      );
    } finally {
      setConfirmingId(null);
      setBusyAction("");
    }
  };

  const handleReturnToHr = async (advance) => {
    if (!advance?.id || confirmingId) return;
    const ok = window.confirm(
      `إرجاع سلفة ${advance.employeeName || "الموظف"} بمبلغ ${formatAmount(advance.amount)} للموارد البشرية لإلغائها؟ لن تُصرف ولن تُعاد للمحاسب العام.`,
    );
    if (!ok) return;

    setConfirmingId(advance.id);
    setBusyAction("return-hr");
    setActionError("");
    try {
      await returnAdvanceToHr(advance.id);
      await loadAdvances();
    } catch (err) {
      setActionError(
        err?.response?.data?.message ||
          err?.message ||
          "تعذر إرجاع السلفة للموارد البشرية",
      );
    } finally {
      setConfirmingId(null);
      setBusyAction("");
    }
  };

  return (
    <div className="adv-disburse" dir="rtl">
      <div className="adv-disburse-header">
        <div>
          <h2 className="adv-disburse-title">تأكيد صرف السلف</h2>
          <p className="adv-disburse-subtitle">
            السلف الموجّهة إليك أو إلى كاشيرية فرعك بانتظار تسليم المبلغ نقداً —
            بعد «تأكيد الاستلام» تُسجَّل السلفة على رصيد الموظف. إذا تعذر الصرف:
            «لم يتم الصرف» تعيدها للمحاسب العام، و«إرجاع للموارد البشرية» لإلغائها.
          </p>
        </div>
        <button
          type="button"
          className="adv-disburse-refresh"
          onClick={loadAdvances}
          disabled={loading || Boolean(confirmingId)}
        >
          تحديث
        </button>
      </div>

      {actionError ? (
        <div className="adv-disburse-error-banner" style={{ marginBottom: 14 }}>
          {actionError}
        </div>
      ) : null}

      {loading ? (
        <div className="adv-disburse-empty">جاري التحميل...</div>
      ) : loadError ? (
        <div className="adv-disburse-error-banner">{loadError}</div>
      ) : advances.length === 0 ? (
        <div className="adv-disburse-empty">
          لا توجد سلف بانتظار تأكيد الصرف حالياً
        </div>
      ) : (
        <div className="adv-disburse-list">
          {advances.map((row) => (
            <div key={row.id} className="adv-disburse-card">
              <div className="adv-disburse-card-header">
                <div>
                  <div className="adv-disburse-employee-name">
                    {row.employeeName || "—"}
                  </div>
                  <div className="adv-disburse-employee-meta">
                    {row.employeeCode || ""}
                    {row.departmentName ? ` · ${row.departmentName}` : ""}
                    {row.branchName ? ` · ${row.branchName}` : ""}
                  </div>
                </div>
                <span className="adv-disburse-badge">بانتظار تأكيد الاستلام</span>
              </div>

              <div className="adv-disburse-details">
                <div className="adv-disburse-detail-row">
                  <span className="adv-disburse-detail-label">المبلغ:</span>
                  <span>{formatAmount(row.amount)}</span>
                </div>
                <div className="adv-disburse-detail-row">
                  <span className="adv-disburse-detail-label">النوع:</span>
                  <span>{advanceTypeLabel(row)}</span>
                </div>
                <div className="adv-disburse-detail-row">
                  <span className="adv-disburse-detail-label">الأقساط:</span>
                  <span>{row.installments}</span>
                </div>
                <div className="adv-disburse-detail-row">
                  <span className="adv-disburse-detail-label">تاريخ التوجيه:</span>
                  <span>{formatDate(row.disbursementAssignedAt)}</span>
                </div>
                {row.reason ? (
                  <div className="adv-disburse-detail-row">
                    <span className="adv-disburse-detail-label">السبب:</span>
                    <span>{row.reason}</span>
                  </div>
                ) : null}
              </div>

              <div className="adv-disburse-actions">
                <button
                  type="button"
                  className="adv-disburse-btn danger"
                  onClick={() => handleReturnToHr(row)}
                  disabled={Boolean(confirmingId)}
                >
                  {confirmingId === row.id && busyAction === "return-hr"
                    ? "جاري الإرجاع..."
                    : "إرجاع للموارد البشرية"}
                </button>
                <button
                  type="button"
                  className="adv-disburse-btn danger"
                  onClick={() => handleNotDisbursed(row)}
                  disabled={Boolean(confirmingId)}
                >
                  {confirmingId === row.id && busyAction === "not-disbursed"
                    ? "جاري الإرجاع..."
                    : "لم يتم الصرف"}
                </button>
                <button
                  type="button"
                  className="adv-disburse-btn"
                  onClick={() => handleConfirm(row)}
                  disabled={Boolean(confirmingId)}
                >
                  {confirmingId === row.id && busyAction === "confirm"
                    ? "جاري التأكيد..."
                    : "تأكيد الاستلام"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
